import { defineStore } from "pinia";
import { reactive } from "vue";

import useFeedSocket from "@/composition/shared/sockets/useFeedSocket.ts";
import type { ContentBaseItem } from "@/types/content.d.ts";

interface SearchUpdateMap {
  contentItems: ContentBaseItem[];
  newItemUrns: Set<string>;
}

const useAutoUpdatesStore = defineStore("search:autoUpdates", () => {
  const {
    startUpFeedSocket,
    subscribeToSearchUpdated,
    unsubscribeOfSearch,
    handleSearchUpdatesOnSpecificSearch,
  } = useFeedSocket();

  const searchUpdates = reactive(new Map<string, SearchUpdateMap>());

  function getSearchUpdate(searchUpdateId: string | null) {
    if (!searchUpdateId) return;

    return searchUpdates.get(searchUpdateId);
  }

  (async () => {
    await startUpFeedSocket();
  })();

  async function addSearchUpdate(searchUpdateId: string | null) {
    if (!searchUpdateId) return;
    await subscribeToSearchUpdated(searchUpdateId);
    if (!searchUpdates.has(searchUpdateId)) {
      searchUpdates.set(searchUpdateId, {
        contentItems: [],
        newItemUrns: new Set(),
      });
    }
    await handleSearchUpdatesOnSpecificSearch(
      searchUpdateId,
      searchUpdates.get(searchUpdateId)?.contentItems,
      searchUpdates.get(searchUpdateId)?.newItemUrns,
    );
  }

  async function removeSearchUpdate(searchUpdateId: string | null) {
    if (!searchUpdateId) return;
    await unsubscribeOfSearch(searchUpdateId);
  }

  function clearIsNew(searchUpdateId: string | null) {
    if (!searchUpdateId) return;
    searchUpdates.get(searchUpdateId)?.newItemUrns.clear();
  }

  function clearContentItems(searchUpdateId: string | null) {
    if (!searchUpdateId) return;
    const searchUpdate = searchUpdates.get(searchUpdateId);
    if (!searchUpdate) return;
    searchUpdate.contentItems = [];
  }

  function removeIsNewItem(searchUpdateId: string | null, itemUrn: string) {
    if (!searchUpdateId) return;
    searchUpdates.get(searchUpdateId)?.newItemUrns.delete(itemUrn);
  }

  return {
    addSearchUpdate,
    searchUpdates,
    getSearchUpdate,
    removeSearchUpdate,
    clearIsNew,
    clearContentItems,
    removeIsNewItem,
  };
});

export default useAutoUpdatesStore;
